import * as React from 'react';

import {
    Occupation,
    Project,
} from "../../../../domain/model/project";
import {
    Business,
    Company,
    EmployeeScale
} from "../../../../domain/model/company";
import {
    companyConfig, projectConfig
} from "../../../../config/language/ja-JP/domain/model_text";
import HeadlineMiddle from "../../../common/atom/text/headline_middle";
import Paragraph from "../../../common/atom/text/paragraph";
import styled from "styled-components";
import Table from "../../../common/atom/table/table";
import Tbody from "../../../common/atom/table/tbody";
import Tr from "../../../common/atom/table/tr";
import ThWithText from "../../../common/atom/table/th_with_text";
import TdWithText from "../../../common/atom/table/td_with_text";
import OuterLink from "../../../common/atom/outer_link";
import {ConstBorderRound, ConstBreakPoint, ConstColor, ConstHeight} from "../../../../config/styles_const/styles_const";
import BadgeBorder from "../../../common/atom/badge/badge_border";
import ParagraphSup from "../../../common/atom/text/paragraph_sup";

interface IProps {
    project: Project;
    company: Company;
}

export const ProjectView: React.FC<IProps> = (props) => {
    const { project, company} = props;

    const projectThumbnailURL: string = project.getThumbnailURL();
    const projectPaymentRange: string =  projectConfig.paymentRangeList[project.paymentRange];
    const projectWorkingHourRange: string = projectConfig.workingHourRangeList[project.workingHourRange];
    const projectPlace: string = projectConfig.placeList[project.place];
    const projectWorkingDuration: string = projectConfig.workingDurationList[project.workingDuration];
    const projectOperatingDate: string = project.getOperatingDateString();
    const companyName: string = company.name;
    const companyIntroduction: string = company.introduction;
    const projectDescription: string = project.description;
    const projectBackground: string = project.background;
    const projectPreferentialExperience: string = project.preferentialExperience;
    const companyWebURL: string = company.webURL;
    const companyWebLink: JSX.Element | string = companyWebURL ?
        <StyleOuterLink href={companyWebURL} target="_blank" rel="noopener">{companyWebURL}</StyleOuterLink>
        : "";
    const establishedAtString: string = company.getEstablishedAtString();
    const companyPlace: string = company.place;
    const business: Business[] = company.business;
    const businessString: string[] = business.map((item: Business, index) => {
        return (
            companyConfig.businessList[item] + " "
        )
    });
    const employeeScale: EmployeeScale = company.employeeScale;
    const employeeScaleString: string = companyConfig.employeeScaleList[employeeScale];
    const representativeName: string = company.representativeName;
    const capitalStockString: string = company.getCapitalStockString();
    const occupations: Occupation[] = project.occupations;
    const isOccupations: boolean = occupations.length !== 0;
    const isHideCompany: boolean = project.isHideCompany;


    return (
        <React.Fragment>

            <ProjectThumbnailWrapper>
                <ProjectThumbnail src={projectThumbnailURL} />
            </ProjectThumbnailWrapper>

            <ProjectInfoWrapper>
                <ProjectInfo>
                    {isOccupations &&
                    <OccupationBadgeWrapper>
                        {occupations.map((occupation) => {
                            return <BadgeBorder
                                key={occupation}>{projectConfig.occupationList.get(occupation)}</BadgeBorder>
                        })}
                    </OccupationBadgeWrapper>
                    }
                    <ProjectItemsWrapper>
                        <ProjectItemWrapper>
                            <ProjectItemTitleWrapper>
                                <ParagraphSup>報酬/月</ParagraphSup>
                            </ProjectItemTitleWrapper>
                            <ProjectItemBodyWrapper>
                                <Paragraph bold={true} >{projectPaymentRange}</Paragraph>
                            </ProjectItemBodyWrapper>
                        </ProjectItemWrapper>
                        <ProjectItemWrapper>
                            <ProjectItemTitleWrapper>
                                <ParagraphSup>時間/月</ParagraphSup>
                            </ProjectItemTitleWrapper>
                            <ProjectItemBodyWrapper>
                                <Paragraph bold={true} >{projectWorkingHourRange}</Paragraph>
                            </ProjectItemBodyWrapper>
                        </ProjectItemWrapper>
                        <ProjectItemWrapper>
                            <ProjectItemTitleWrapper>
                                <ParagraphSup>場所</ParagraphSup>
                            </ProjectItemTitleWrapper>
                            <ProjectItemBodyWrapper>
                                <Paragraph bold={true} >{projectPlace}</Paragraph>
                            </ProjectItemBodyWrapper>
                        </ProjectItemWrapper>
                        <ProjectItemWrapper>
                            <ProjectItemTitleWrapper>
                                <ParagraphSup>稼働期間</ParagraphSup>
                            </ProjectItemTitleWrapper>
                            <ProjectItemBodyWrapper>
                                <Paragraph bold={true} >{projectWorkingDuration}</Paragraph>
                            </ProjectItemBodyWrapper>
                        </ProjectItemWrapper>
                        <ProjectItemWrapper>
                            <ProjectItemTitleWrapper>
                                <ParagraphSup>稼働開始希望時期</ParagraphSup>
                            </ProjectItemTitleWrapper>
                            <ProjectItemBodyWrapper>
                                <Paragraph bold={true} >{projectOperatingDate}</Paragraph>
                            </ProjectItemBodyWrapper>
                        </ProjectItemWrapper>
                    </ProjectItemsWrapper>
                </ProjectInfo>
            </ProjectInfoWrapper>

            {!isHideCompany && (
                <ProjectSubTitleWrapper>
                    <HeadlineMiddle>
                        どのような会社か
                    </HeadlineMiddle>
                </ProjectSubTitleWrapper>
            )
            }
            {!isHideCompany && (
                <ProjectSubBodyWrapper>
                    <Paragraph >
                        {companyIntroduction}
                    </Paragraph>
                </ProjectSubBodyWrapper>
            )
            }
            <ProjectSubTitleWrapper>
                <HeadlineMiddle>
                    今回の募集内容
                </HeadlineMiddle>
            </ProjectSubTitleWrapper>
            <ProjectSubBodyWrapper>
                <ProjectSubBody>
                    {projectDescription}
                </ProjectSubBody>
            </ProjectSubBodyWrapper>

            <ProjectSubTitleWrapper>
                <HeadlineMiddle>
                    募集背景
                </HeadlineMiddle>
            </ProjectSubTitleWrapper>
            <ProjectSubBodyWrapper>
                <ProjectSubBody>
                    {projectBackground}
                </ProjectSubBody>
            </ProjectSubBodyWrapper>

            <ProjectSubTitleWrapper>
                <HeadlineMiddle>
                    求める経験
                </HeadlineMiddle>
            </ProjectSubTitleWrapper>
            <ProjectSubBodyWrapper>
                <ProjectSubBody>
                    {projectPreferentialExperience}
                </ProjectSubBody>
            </ProjectSubBodyWrapper>

            <Table >
                <Tbody>
                    <Tr>
                        <ThWithText >企業名</ThWithText>
                        <TdWithText colSpan={3}>{companyName}</TdWithText>
                    </Tr>
                    <Tr>
                        <ThWithText >URL</ThWithText>
                        <TdWithText colSpan={3}>{companyWebLink}</TdWithText>
                    </Tr>
                    <Tr>
                        <ThWithText >設立年月日</ThWithText>
                        <TdWithText colSpan={3}>{establishedAtString}</TdWithText>
                    </Tr>
                    <Tr>
                        <ThWithText >所在地</ThWithText>
                        <TdWithText colSpan={3}>{companyPlace}</TdWithText>
                    </Tr>
                    <Tr>
                        <ThWithText >業種</ThWithText>
                        <TdWithText colSpan={3}>{businessString}</TdWithText>
                    </Tr>
                    <Tr>
                        <ThWithText >従業員規模</ThWithText>
                        <TdWithText colSpan={3}>{employeeScaleString}</TdWithText>
                    </Tr>
                    <Tr>
                        <ThWithText >代表取締役</ThWithText>
                        <TdWithText colSpan={3}>{representativeName}</TdWithText>
                    </Tr>
                    <Tr>
                        <ThWithText >資本金</ThWithText>
                        <TdWithText colSpan={3}>{capitalStockString}</TdWithText>
                    </Tr>
                </Tbody>
            </Table>

        </React.Fragment>

    )

};

export default ProjectView;

const ProjectThumbnailWrapper = styled.div`
    margin-top: 85px;
    margin-bottom: 10px;
`;

const ProjectThumbnail = styled.img`
    height: ${ConstHeight.ProjectThumbnail};
    width: 100%;
    border-radius: ${ConstBorderRound.CornerRoundLeftTopRightTop};
    object-fit: cover;
`;

const ProjectInfoWrapper = styled.div`
    margin-bottom: 40px;
`;

const ProjectInfo = styled.div`
    padding: 20px;
    border: 1px solid ${ConstColor.PlaceholderAndBorder};
    border-radius: ${ConstBorderRound.CornerRoundAll};
`;

const OccupationBadgeWrapper = styled.div`
    margin-bottom: 10px;
`;

const ProjectItemsWrapper = styled.div`
    @media screen and (min-width: ${ConstBreakPoint.Default}) {
        display: flex;
        flex-wrap: wrap;  
    }
`;

const ProjectItemWrapper = styled.div`
    display: flex;
    flex-wrap: no-wrap;  
    align-items: baseline;
    margin-right: 30px;
`;

const ProjectItemTitleWrapper = styled.div`
    margin-right: 10px;
`;

const ProjectItemBodyWrapper = styled.div`
`;

const ProjectSubTitleWrapper = styled.div`
    margin-bottom: 20px;
`;

const ProjectSubBodyWrapper = styled.div`
    margin-bottom: 50px;
`;

const ProjectSubBody = styled(Paragraph)`
    word-wrap: break-word;
    white-space: pre-wrap;
`;

const StyleOuterLink = styled(OuterLink)`
    color: ${ConstColor.Corporate};
`;


// WEBPACK FOOTER //
// ./src/presentation/common/template/page_project/project_veiw.tsx